import { useQuery } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import logo from "@/assets/logo.png";

async function fetchStatus() {
  const response = await fetch(`${import.meta.env.VITE_API_URL ?? "http://localhost:3001"}/status`);
  if (!response.ok) throw new Error("Could not connect to the API");
  return response.json();
}

type StatusState = "ok" | "error" | "unknown";

function StatusDot({ state }: { state: StatusState }) {
  const colors: Record<StatusState, string> = {
    ok: "bg-green-500",
    error: "bg-red-500",
    unknown: "bg-yellow-400",
  };
  return <span className={`inline-block size-3 rounded-full ${colors[state]}`} />;
}

export default function StatusPage() {
  const { data, error, isLoading } = useQuery({
    queryKey: ["status"],
    queryFn: fetchStatus,
    refetchInterval: 5000,
  });

  const apiState: StatusState = isLoading ? "unknown" : error ? "error" : data?.api?.status === "ok" ? "ok" : "error";
  const dbState: StatusState = isLoading || error ? "unknown" : data?.database?.status === "ok" ? "ok" : "error";

  return (
    <div className="min-h-screen bg-background p-8">
      <div className="max-w-2xl mx-auto space-y-6">
        <img src={logo} alt="Logo" className="h-10 w-auto" />
        <div>
          <h1 className="text-2xl font-bold">Estado del sistema</h1>
          <p className="text-muted-foreground text-sm mt-1">
            Actualización automática cada 5 segundos
          </p>
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">API</CardTitle>
              <StatusDot state={apiState} />
            </CardHeader>
            <CardContent>
              <p className="text-xs text-muted-foreground">
                {apiState === "ok" ? "Operativo" : apiState === "error" ? (error ? "Sin conexión" : "Error") : "Verificando..."}
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Base de datos</CardTitle>
              <StatusDot state={dbState} />
            </CardHeader>
            <CardContent>
              <p className="text-xs text-muted-foreground break-words">
                {dbState === "ok"
                  ? "Operativo"
                  : dbState === "error"
                  ? (data?.database?.error ?? "Error")
                  : "Verificando..."}
              </p>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
